import type { ColumnSort } from '@plato/ui/components/data-table';
import { useCallback, useMemo } from 'react';

import { SortOrder } from '../../../server/types/list-view-types.ts';

export type SortFilter<TField, TDirection extends 'ASC' | 'DESC' = SortOrder> = {
    field: TField;
    direction: TDirection;
};

export type UseGenericListViewSortingHook<TField extends string> = [
    sorting: ColumnSort<TField> | undefined,
    onSortingChange: (sort: ColumnSort | undefined) => void,
];

export function useGenericListViewSorting<
    TField extends string,
    TDirection extends 'ASC' | 'DESC' = SortOrder,
>(
    sortingFilter: SortFilter<TField, TDirection> | null | undefined,
    setSortingFilter: (sortingFilter: SortFilter<TField, TDirection> | undefined) => void,
    sortFields: readonly TField[],
): UseGenericListViewSortingHook<TField> {
    const field = sortingFilter?.field;
    const direction = sortingFilter?.direction;

    const sorting = useMemo<ColumnSort<TField> | undefined>(
        () =>
            field === undefined || direction === undefined
                ? undefined
                : { id: field, direction: direction === SortOrder.DESC ? 'desc' : 'asc' },
        [field, direction],
    );

    const onSortingChange = useCallback(
        (sort: ColumnSort | undefined) => {
            if (sort === undefined) {
                setSortingFilter(undefined);
                return;
            }

            const sortField = sortFields.find((candidate) => candidate === sort.id);
            if (sortField !== undefined) {
                setSortingFilter({
                    field: sortField,
                    direction: (sort.direction === 'desc' ? SortOrder.DESC : SortOrder.ASC) as TDirection,
                });
            }
        },
        [setSortingFilter, sortFields],
    );

    return [sorting, onSortingChange];
}
